<template>
  <div v-if="localEvent">
    <template v-if="isCalendlyEvent">
      <div class="mb-4 small">
        {{ $t('EVENTS.TEXT.CALENDLY_NON_EDITABLE_FIELDS') }}
        <a
          href="https://calendly.com/app/scheduled_events/user/me"
          target="_blank"
        >
          {{ $t('EVENTS.TEXT.CLICK_HERE_TO_EDIT_IN_CALENDLY') }}
        </a>
      </div>
      <form @submit.prevent="save">
        <div class="row">
          <div class="col-sm-12 col-md-8">
            <HMTextInput
              type="text"
              rules="required"
              :label="$t('EVENTS.LABEL.EVENT_NAME')"
              v-model="localEvent.event_name"
              name="Event Name"
            />
          </div>
          <div class="col-sm-12 col-md-4">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.EVENT_TYPE') }}</label>
              <p class="mt-2">
                <span :class="selectedEventType.icon" class="mr-1"></span>
                <span class="ml-1 c-charcoal">{{
                  $t(selectedEventType.label)
                }}</span>
              </p>
            </div>
          </div>
        </div>
        <div class="row" v-if="hasMultipleZoomConnections">
          <div class="col-12">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.ZOOM_CONNECTION') }}</label>
              <v-select
                :options="zoomInfo"
                label="name"
                code="id"
                v-model="localEvent.zoom_connection"
                :reduce="c => c.id"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.DATE') }}</label>
              <p class="mt-2">
                <span class="c-charcoal">{{
                  formatDate(localEvent.date)
                }}</span>
              </p>
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <label>{{ $t('EVENTS.LABEL.STARTS') }}</label>
            <p class="mt-2">
              <span class="c-charcoal">{{
                formatTime(localEvent.event_start_datetime)
              }}</span>
            </p>
          </div>
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.DURATION') }}</label>
              <p class="mt-2">
                <span class="c-charcoal" v-if="localEvent.duration_hours > 0">
                  {{
                    $tc('GENERAL.LABEL.HOUR_VARIANT', localEvent.duration_hours)
                  }}
                </span>
                <span
                  class="ml-2 c-charcoal"
                  v-if="localEvent.duration_minutes > 0"
                >
                  {{
                    $tc(
                      'GENERAL.LABEL.MINUTE_VARIANT',
                      localEvent.duration_minutes
                    )
                  }}
                </span>
              </p>
            </div>
          </div>
        </div>
        <template>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label>{{ $t('EVENTS.LABEL.PRODUCT') }}</label>
                <p class="mt-2">
                  <span
                    class="c-charcoal"
                    v-if="localEvent.products.length > 0"
                  >
                    {{ localEvent.products[0].product_name }}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label>{{ $t('EVENTS.LABEL.STUDENT') }}</label>
                <p class="mt-2">
                  <span class="c-charcoal" v-if="localEvent.student">
                    {{ localEvent.student.full_name }} ({{
                      localEvent.student.email
                    }})
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label>{{
                  $t('EVENTS.LABEL.WHICH_INSTRUCTORS_WILL_LEAD_THIS_EVENT')
                }}</label>
                <v-select
                  :options="allInstructors"
                  multiple
                  label="name"
                  code="id"
                  v-model="localEvent.instructors"
                />
                <div v-if="maxInstructors === 1" class="d-inline-flex">
                  <span class="c-coral pointer small mr-2">
                    {{ $t('EVENTS.LABEL.ADD_SUBSTITUTES') }}
                  </span>
                  <HMUpgradeLock text="EVENTS.LABEL.ADD_SUBSTITUTES_LOCKED" />
                </div>
                <span
                  v-else-if="addSubstitutesLinkVisible"
                  class="c-coral pointer small"
                  @click="substitutesVisible = true"
                >
                  {{ $t('EVENTS.LABEL.ADD_SUBSTITUTES') }}
                </span>
                <div
                  v-if="displayInstructorSubstitutes"
                  class="form-group mt-3"
                >
                  <label>{{
                    $t(
                      'EVENTS.LABEL.WHICH_INSTRUCTORS_WILL_SUBSTITUTE_THIS_EVENT'
                    )
                  }}</label>
                  <v-select
                    :options="allInstructors"
                    multiple
                    label="name"
                    code="id"
                    v-model="localEvent.substitute_instructors"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <HMHTMLEditor
                  class="mt-1"
                  v-model="localEvent.event_description_new"
                  :htmlValue="localEvent.event_description"
                  :title="$t('GENERAL.LABEL.DESCRIPTION')"
                  :addContentText="$t('EVENTS.TEXT.ADD_TEXT_DESCRIPTION')"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">{{
                  $t('EVENTS.LABEL.EMAIL_NOTIFICATIONS')
                }}</label>
                <HMSwitch v-model="localEvent.email_notifications_enabled" />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">{{
                  $t('EVENTS.LABEL.SMS_NOTIFICATIONS')
                }}</label>
                <HMSwitch v-model="localEvent.sms_notifications_enabled" />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label
                  class="c-charcoal m-0 mr-4"
                  v-tooltip="{
                    content: $t('EVENTS.TEXT.POST_EVENT_RECORDING_TOOLTIP'),
                    delay: 100,
                    placement: 'top',
                    container: 'body',
                    trigger: 'click hover',
                  }"
                >
                  {{ $t('EVENTS.LABEL.MAKE_POST_EVENT_RECORDING_AVAILABLE') }}
                </label>
                <HMSwitch v-model="localEvent.post_event_recording_available" />
                <div v-if="localEvent.post_event_recording_available">
                  <span class="c-charcoal ml-3">for</span>
                  <b-form-select
                    v-model="localEvent.recording_expiration_days"
                    class="ml-3 w-auto"
                  >
                    <b-form-select-option
                      v-for="days in [
                        1,
                        2,
                        3,
                        4,
                        5,
                        6,
                        7,
                        8,
                        9,
                        10,
                        11,
                        12,
                        13,
                        14,
                      ]"
                      :key="days"
                      :value="days"
                    >
                      {{ $tc('GENERAL.LABEL.DAY_VARIANT', days) }}
                    </b-form-select-option>
                  </b-form-select>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">
                  {{
                    $t(
                      'EVENTS.LABEL.COPY_EVENT_DESCRIPTION_TO_RECORDING_DESCRIPTION'
                    )
                  }}
                </label>
                <HMSwitch
                  v-model="localEvent.copy_event_description_to_recording"
                />
              </div>
            </div>
          </div>
        </template>
      </form>
    </template>
    <template v-else>
      <form @submit.prevent="$emit('save', localEvent)">
        <div class="row">
          <div class="col-sm-12 col-md-7">
            <HMTextInput
              type="text"
              rules="required"
              :label="$t('EVENTS.LABEL.EVENT_NAME')"
              v-model="localEvent.event_name"
              name="Event Name"
            />
          </div>
          <div class="col-sm-12 col-md-5">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.EVENT_TYPE') }}</label>
              <ValidationProvider
                rules="required"
                :name="$t('EVENTS.LABEL.EVENT_TYPE')"
                v-slot="{ errors }"
              >
                <v-select
                  :class="{ 'is-invalid': errors.length }"
                  :options="eventTypes"
                  v-model="selectedEventType"
                  @input="setSelectedEventType($event)"
                >
                  <template v-slot:selected-option="option">
                    <span :class="option.icon" class="mr-1"></span>
                    <span class="d-inline-block text-truncate">{{
                      option.label
                    }}</span>
                  </template>
                  <template v-slot:option="option">
                    <span :class="option.icon"></span>
                    {{ option.label }}
                  </template>
                </v-select>
                <p class="invalid-feedback d-block">{{ errors[0] }}</p>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <div class="row" v-if="isInPersonEvent">
          <div class="col-sm-12">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.EVENT_LOCATION') }}</label>
              <HMTextInput
                type="text"
                v-model="localEvent.location_address"
                name="Event location"
                :rules="`${localEvent.is_www_event ? 'required' : ''}`"
              />
            </div>
          </div>
        </div>
        <div class="row" v-if="hasMultipleZoomConnections">
          <div class="col-12">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.ZOOM_CONNECTION') }}</label>
              <v-select
                :options="zoomInfo"
                label="name"
                code="id"
                v-model="localEvent.zoom_connection"
                :reduce="c => c.id"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.DATE') }}</label>
              <!-- TODO locale -->
              <b-form-datepicker
                v-model="localEvent.date"
                :locale="$i18n.locale"
                :date-format-options="{
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                  weekday: 'short',
                }"
              />
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <label>{{ $t('EVENTS.LABEL.STARTS') }}</label>
            <div class="form-group">
              <ValidationProvider
                rules="required"
                :name="$t('EVENTS.LABEL.STARTS')"
                v-slot="{ errors }"
              >
                <HMTimePicker v-model="localEvent.start_time" />
                <p class="invalid-feedback d-block">{{ errors[0] }}</p>
              </ValidationProvider>
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label>{{ $t('EVENTS.LABEL.SET_DURATION') }}</label>
              <div class="d-flex flex-row flex-grow-1">
                <ValidationProvider
                  rules="duration:@minutes"
                  name="hours"
                  v-slot="{ errors }"
                  class="d-flex flex-grow-1 flex-column mr-3"
                >
                  <b-form-select
                    v-model="localEvent.duration_hours"
                    class="mr-3"
                  >
                    <b-form-select-option
                      v-for="hour in [
                        0,
                        1,
                        2,
                        3,
                        4,
                        5,
                        6,
                        7,
                        8,
                        9,
                        10,
                        11,
                        12,
                        13,
                        14,
                        15,
                        16,
                        17,
                        18,
                        19,
                        20,
                        21,
                        22,
                        23,
                        24,
                      ]"
                      :key="hour"
                      :value="hour"
                    >
                      {{ $tc('GENERAL.LABEL.HOUR_VARIANT', hour) }}
                    </b-form-select-option>
                  </b-form-select>
                  <p class="invalid-feedback d-block">{{ $t(errors[0]) }}</p>
                </ValidationProvider>
                <ValidationProvider
                  rules="duration:@hours"
                  name="minutes"
                  class="d-flex flex-grow-1 flex-column"
                >
                  <b-form-select v-model="localEvent.duration_minutes" class="">
                    <b-form-select-option
                      v-for="minute in [
                        0,
                        5,
                        10,
                        15,
                        20,
                        25,
                        30,
                        35,
                        40,
                        45,
                        50,
                        55,
                      ]"
                      :key="minute"
                      :value="minute"
                    >
                      {{ $tc('GENERAL.LABEL.MINUTE_VARIANT', minute) }}
                    </b-form-select-option>
                  </b-form-select>
                </ValidationProvider>
              </div>
            </div>
          </div>
        </div>
        <template v-if="isNewEvent">
          <div class="row">
            <div class="col-sm-6 col-md-3">
              <div class="form-group">
                <HMCheckBox class="col" v-model="localEvent.is_repeating_event">
                  {{ $t('EVENTS.LABEL.EVENT_REPEATS') }}
                </HMCheckBox>
              </div>
            </div>
          </div>
          <div class="row" v-if="localEvent.is_repeating_event">
            <div class="col-12">
              <div class="form-group">
                <div
                  class="bg-lightest-gray border border-white rounded px-4 py-3"
                >
                  <div class="row">
                    <div class="col-sm-12 col-lg-8">
                      <div class="form-group">
                        <label>{{ $t('EVENTS.LABEL.REPEATS_ON') }}</label>
                        <div class="d-flex flex-row pt-2">
                          <HMToggleButton
                            class="mr-2"
                            v-for="day in daysInWeek"
                            :key="day.value"
                            v-model="localEvent.days_of_week[day.value]"
                          >
                            {{ day.display_name }}
                          </HMToggleButton>
                        </div>
                      </div>
                    </div>
                    <div class="col-sm-12 col-lg-4">
                      <div class="form-group">
                        <label>{{ $t('EVENTS.LABEL.REPEATS_UNTIL') }}</label>
                        <ValidationProvider
                          rules="required"
                          :name="$t('EVENTS.LABEL.REPEATS_UNTIL')"
                          v-slot="{ errors }"
                        >
                          <!-- TODO locale -->
                          <b-form-datepicker
                            v-model="localEvent.schedule_end_date"
                            :locale="$i18n.locale"
                            :min="minDate"
                            :max="maxDate"
                          />
                          <p class="invalid-feedback d-block">
                            {{ errors[0] }}
                          </p>
                        </ValidationProvider>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="row" v-if="isRepeatingEvent">
            <div class="col-12">
              <div class="form-group">
                <div
                  class="bg-lightest-gray border border-white rounded px-4 py-3"
                >
                  <div class="row">
                    <div class="col-sm-12 col-lg-4">
                      <div class="form-group mb-0">
                        <label>{{ $t('EVENTS.LABEL.REPEATS_UNTIL') }}</label>
                        <ValidationProvider
                          rules="required"
                          :name="$t('EVENTS.LABEL.REPEATS_UNTIL')"
                          v-slot="{ errors }"
                        >
                          <!-- TODO locale -->
                          <b-form-datepicker
                            v-model="localEvent.schedule_end_date"
                            :locale="$i18n.locale"
                            :min="minDate"
                            :max="maxDate"
                          />
                          <p class="invalid-feedback d-block">
                            {{ errors[0] }}
                          </p>
                        </ValidationProvider>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-if="!isPrivateEvent && eventProducts && localEvent">
          <div class="row" v-if="!localEvent.is_free_event">
            <div class="col-sm-12">
              <div class="form-group">
                <label>
                  {{ $t('EVENTS.LABEL.WHICH_PRODUCTS_CONTAIN_THIS_EVENT') }}
                </label>
                <v-select
                  :options="eventProducts"
                  multiple
                  label="product_name"
                  code="id"
                  v-model="localEvent.products"
                />
                <p
                  class="invalid-feedback d-block"
                  v-if="
                    (!localEvent.products ||
                      localEvent.products.length === 0) &&
                    !localEvent.is_punchable_event
                  "
                  v-html="
                    sanitize(
                      $t('EVENTS.TEXT.EVENT_PRODUCTS_OR_CLASSCARD_SELECTED')
                    )
                  "
                ></p>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label>{{
                  $t('EVENTS.LABEL.WHICH_INSTRUCTORS_WILL_LEAD_THIS_EVENT')
                }}</label>
                <v-select
                  :options="allInstructors"
                  multiple
                  label="name"
                  code="id"
                  v-model="localEvent.instructors"
                />
                <div v-if="maxInstructors === 1" class="d-inline-flex">
                  <span class="c-coral pointer small mr-2">
                    {{ $t('EVENTS.LABEL.ADD_SUBSTITUTES') }}
                  </span>
                  <HMUpgradeLock text="EVENTS.LABEL.ADD_SUBSTITUTES_LOCKED" />
                </div>
                <span
                  v-else-if="addSubstitutesLinkVisible"
                  class="c-coral pointer small"
                  @click="substitutesVisible = true"
                >
                  {{ $t('EVENTS.LABEL.ADD_SUBSTITUTES') }}
                </span>
                <div
                  v-if="displayInstructorSubstitutes"
                  class="form-group mt-3"
                >
                  <label>{{
                    $t(
                      'EVENTS.LABEL.WHICH_INSTRUCTORS_WILL_SUBSTITUTE_THIS_EVENT'
                    )
                  }}</label>
                  <v-select
                    :options="allInstructors"
                    multiple
                    label="name"
                    code="id"
                    v-model="localEvent.substitute_instructors"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <HMHTMLEditor
                  class="mt-1"
                  v-model="localEvent.event_description_new"
                  :htmlValue="localEvent.event_description"
                  :title="$t('GENERAL.LABEL.DESCRIPTION')"
                  :addContentText="$t('EVENTS.TEXT.ADD_TEXT_DESCRIPTION')"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <label>
                  {{ $t('PRODUCTS.LABELS.COVER_PHOTO') }}
                </label>
                <HMImageUpload
                  inputName="cover-photo"
                  :title="$t('PRODUCTS.TEXT.UPLOAD_COVER_PHOTO')"
                  :description="
                    $t('PRODUCTS.TEXT.UPLOAD_IMAGE_TEXT', {
                      width: 1920,
                      height: 600,
                    })
                  "
                  :imageDescription="$t('PRODUCTS.LABELS.COVER_PHOTO')"
                  v-model="localEvent.cover_photo"
                  :canvaWidth="1920"
                  :canvaHeight="600"
                  :focalPointPicker="true"
                />
              </div>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <div>
                  <label>
                    {{ $t('MEDIA_LIBRARY.TITLE.ATTACHMENTS') }}
                  </label>
                </div>
                <div class="form-group">
                  <HMFileUpload
                    :value="null"
                    @input="addFile"
                    :title="$t('MEDIA_LIBRARY.TITLE.UPLOAD_ATTACHMENT')"
                    :description="
                      $t('MEDIA_LIBRARY.TEXT.SUPPORTED_ATTACHMENTS_FORMATS')
                    "
                    accept="image/*,audio/*,application/pdf,application/msword,application/vnd.ms-excel,application/vnd.ms-powerpoint,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.slideshow"
                    inputName="attachment"
                  />
                  <HMSimpleActionCard
                    class="mt-3"
                    :prefixIcon="['fas', 'image']"
                    v-for="(file, index) in localEvent.attachments"
                    :key="file.id"
                    :title="cleanedFileName(file.file_name)"
                    @click="downloadAttachment(file)"
                  >
                    <template slot="actions">
                      <font-awesome-icon
                        class="c-coral ml-2"
                        :icon="['far', 'trash']"
                        @click="removeFile(index)"
                      />
                    </template>
                  </HMSimpleActionCard>
                  <div class="mt-2">
                    {{ $t('PRODUCTS.TEXT.ATTACHMENTS_DESCRIPTION') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <hr class="mb-4" />
            </div>
            <div class="col-sm-12" v-if="canShowFreeEventOption">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">
                  {{ $t('EVENTS.LABEL.IS_THIS_FREE_PUBLIC_EVENT') }}
                </label>
                <div
                  v-tooltip="{
                    content: getFreeEventDisabledTooltip,
                    delay: 100,
                    placement: 'top',
                    container: 'body',
                    trigger: 'click hover',
                  }"
                >
                  <HMSwitch
                    :disabled="hasRegistrations"
                    v-model="localEvent.is_free_event"
                  />
                </div>
              </div>
            </div>
            <div class="col-sm-12" v-if="localEvent.is_free_event">
              <div class="form-group">
                <HMCheckBox v-model="localEvent.require_first_name">
                  {{ $t('EVENTS.LABEL.REQUIRE_FIRST_NAME') }}
                </HMCheckBox>
                <HMCheckBox v-model="localEvent.require_last_name" class="mt-4">
                  {{ $t('EVENTS.LABEL.REQUIRE_LAST_NAME') }}
                </HMCheckBox>
              </div>
              <div
                class="form-group email-providers convertkit"
                v-if="flodeskSegments.length && isUsingFlodesk"
              >
                <h5>{{ $t('PRODUCTS.LABELS.FLODESK_SEGMENT') }}</h5>
                <v-select
                  :options="flodeskSegments"
                  label="name"
                  code="id"
                  v-model="localEvent.flodesk_segment"
                  :reduce="segment => segment.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.FLODESK_SEGMENT_DESCRIPTION')
                }}</span>
              </div>
              <div
                class="form-group email-providers convertkit"
                v-if="convertKitTags.length && isUsingConvertKit"
              >
                <h5>{{ $t('PRODUCTS.LABELS.CONVERTKIT_TAG') }}</h5>
                <v-select
                  :options="convertKitTagsMapped"
                  label="name"
                  code="id"
                  v-model="localEvent.convertkit_tag"
                  :reduce="tag => tag.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.CONVERTKIT_TAG_DESCRIPTION')
                }}</span>
              </div>
              <div
                class="form-group email-providers mailchimp"
                v-if="mailChimpLists.length && isUsingMailChimp"
              >
                <h5>{{ $t('PRODUCTS.LABELS.MAILCHIMP_LIST') }}</h5>
                <v-select
                  :options="mailChimpLists"
                  label="name"
                  code="id"
                  v-model="localEvent.mailchimp_list"
                  :reduce="list => list.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.MAILCHIMP_LIST_DESCRIPTION')
                }}</span>
                <HMTextInput
                  v-if="localEvent.mailchimp_list"
                  class="mt-3"
                  type="text"
                  v-model="localEvent.mailchimp_tag"
                  :placeholder="$t('PRODUCTS.LABELS.OPTIONAL_MAILCHIMP_TAG')"
                  :label="$t('EVENTS.TEXT.USE_MAILCHIMP_TAG')"
                  name="Mailchimp tag"
                />
              </div>
              <hr class="mb-4" />
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">
                  {{ $t('EVENTS.LABEL.REGISTRATION') }}
                </label>
                <div
                  v-tooltip="{
                    content: localEvent.is_free_event
                      ? $t('EVENTS.TEXT.FREE_EVENT_REQUIRES_REGISTRATIONS')
                      : null,
                    delay: 100,
                    placement: 'top',
                    container: 'body',
                    trigger: 'click hover',
                  }"
                >
                  <HMToggleButtonGroup
                    v-model="localEvent.registration_required"
                    :disabled="localEvent.is_free_event"
                  >
                    <template slot="falseState">
                      <span>
                        {{ $tc('GENERAL.LABEL.REQUIRED_VARIANT', 0) }}
                      </span>
                    </template>
                    <template slot="trueState">
                      <span>
                        {{ $tc('GENERAL.LABEL.REQUIRED_VARIANT', 1) }}
                      </span>
                    </template>
                  </HMToggleButtonGroup>
                </div>
              </div>
            </div>
            <div class="col-sm-12" v-if="localEvent.registration_required">
              <div class="form-group">
                <HMCheckBox v-model="localEvent.hide_registration_info">
                  {{ $t('EVENTS.LABEL.HIDE_REGISTRATIONS_INFO') }}
                </HMCheckBox>
              </div>
            </div>
          </div>
          <div class="row" v-if="localEvent.registration_required">
            <div class="col-sm-12 col-md-4">
              <div class="form-group">
                <label>{{
                  $t('EVENTS.LABEL.LIMIT_NUMBER_OF_REGISTRANTS')
                }}</label>
                <b-form-select
                  v-model="localEvent.registration_limit"
                  class="mr-3"
                  :disabled="isRegistrationLimitDisabled"
                >
                  <b-form-select-option value="0">{{
                    $tc('EVENTS.LABEL.STUDENT_LIMIT_VARIANT', 0)
                  }}</b-form-select-option>
                  <b-form-select-option
                    v-for="limit in registrationLimits"
                    :key="limit"
                    :value="limit"
                  >
                    {{ $tc('EVENTS.LABEL.STUDENT_LIMIT_VARIANT', limit) }}
                  </b-form-select-option>
                </b-form-select>
              </div>
            </div>
            <div class="col-sm-12 col-md-4">
              <div class="form-group">
                <label>{{ $t('EVENTS.LABEL.REGISTRATION_CLOSES') }}</label>
                <b-form-select
                  v-model="localEvent.registration_closes_minutes_before_start"
                  class="mr-3"
                >
                  <b-form-select-option
                    v-for="option in durationOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }} {{ $t('EVENTS.LABEL.BEFORE_START') }}
                  </b-form-select-option>
                </b-form-select>
              </div>
            </div>
            <div class="col-sm-12 col-md-4">
              <div class="form-group">
                <label>{{ $t('EVENTS.LABEL.CLOSE_UNREGISTRATION') }}</label>
                <b-form-select
                  v-model="
                    localEvent.unregistration_closes_minutes_before_start
                  "
                  class="mr-3"
                >
                  <b-form-select-option
                    v-for="option in durationOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }} {{ $t('EVENTS.LABEL.BEFORE_START') }}
                  </b-form-select-option>
                </b-form-select>
              </div>
            </div>
            <div class="col-12" v-if="!localEvent.is_free_event">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="m-0 mr-4">{{
                  $t('EVENTS.LABEL.WAITLIST_AUTO_NOTIFY')
                }}</label>
                <HMSwitch v-model="localEvent.auto_notify_waitlist"></HMSwitch>
              </div>
            </div>
            <div class="col-12">
              <hr class="mb-4" />
            </div>
          </div>
          <div class="row" v-if="!localEvent.is_free_event">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">ClassCard&trade;</label>
                <HMToggleButtonGroup v-model="localEvent.is_punchable_event">
                  <template slot="falseState">
                    <span>{{
                      $tc('GENERAL.LABEL.ALLOWED_VARIANT', 0)
                    }}</span> </template
                  >>
                  <template slot="trueState">
                    <span>{{
                      $tc('GENERAL.LABEL.ALLOWED_VARIANT', 1)
                    }}</span> </template
                  >>
                </HMToggleButtonGroup>
              </div>
            </div>
          </div>
          <div
            class="row"
            v-if="localEvent.is_punchable_event && !localEvent.is_free_event"
          >
            <div class="col-sm-12">
              <div class="form-group">
                <label>
                  {{ $t('EVENTS.LABEL.LIMIT_EVENT_TO_THESE_CLASSCARDS') }}
                </label>
                <v-select
                  :options="eventClassCardProducts"
                  multiple
                  label="product_name"
                  code="id"
                  v-model="localEvent.classcards"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">{{
                  $t('EVENTS.LABEL.EMAIL_NOTIFICATIONS')
                }}</label>
                <HMToggleButtonGroup
                  v-model="localEvent.email_notifications_enabled"
                >
                  <template slot="falseState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 0)
                    }}</span> </template
                  >>
                  <template slot="trueState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 1)
                    }}</span> </template
                  >>
                </HMToggleButtonGroup>
              </div>
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">{{
                  $t('EVENTS.LABEL.SMS_NOTIFICATIONS')
                }}</label>
                <HMToggleButtonGroup
                  v-model="localEvent.sms_notifications_enabled"
                >
                  <template slot="falseState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 0)
                    }}</span> </template
                  >>
                  <template slot="trueState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 1)
                    }}</span> </template
                  >>
                </HMToggleButtonGroup>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label
                  class="c-charcoal m-0 mr-4"
                  v-tooltip="{
                    content: $t('EVENTS.TEXT.POST_EVENT_RECORDING_TOOLTIP'),
                    delay: 100,
                    placement: 'top',
                    container: 'body',
                    trigger: 'click hover',
                  }"
                >
                  {{ $t('EVENTS.LABEL.MAKE_POST_EVENT_RECORDING_AVAILABLE') }}
                </label>
                <HMSwitch v-model="localEvent.post_event_recording_available" />
                <div v-if="localEvent.post_event_recording_available">
                  <span class="c-charcoal ml-3">for</span>
                  <b-form-select
                    v-model="localEvent.recording_expiration_days"
                    class="ml-3 w-auto"
                  >
                    <b-form-select-option
                      v-for="days in [
                        1,
                        2,
                        3,
                        4,
                        5,
                        6,
                        7,
                        8,
                        9,
                        10,
                        11,
                        12,
                        13,
                        14,
                      ]"
                      :key="days"
                      :value="days"
                    >
                      {{ $tc('GENERAL.LABEL.DAY_VARIANT', days) }}
                    </b-form-select-option>
                  </b-form-select>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">
                  {{
                    $t(
                      'EVENTS.LABEL.COPY_EVENT_DESCRIPTION_TO_RECORDING_DESCRIPTION'
                    )
                  }}
                </label>
                <HMSwitch
                  v-model="localEvent.copy_event_description_to_recording"
                />
              </div>
            </div>
          </div>
          <div
            class="row"
            v-if="!localEvent.is_free_event && canCreateWWWEvent"
          >
            <div class="col-sm-12">
              <hr />
            </div>
            <div class="col-sm-12">
              <div
                class="www-card d-flex flex-row justify-content-between align-items-start flex-wrap rounded w-100 p-4"
              >
                <div class="d-flex mr-md-4 flex-0">
                  <img src="@/assets/images/wellwellwell1.png" class="mr-2" />
                  <img src="@/assets/images/wellwellwell2.png" />
                </div>
                <div class="d-flex flex-column flex-1 mr-4 mt-4 mt-md-0">
                  <img
                    class="www-logo"
                    src="@/assets/images/wellwellwell-logo.png"
                  />
                  <div
                    class="c-charcoal mt-3"
                    v-html="
                      sanitize($t('EVENTS.TEXT.WELL_WELL_WELL_BANNER_TEXT'))
                    "
                  ></div>
                </div>
                <div class="d-flex flex-0">
                  <HMSwitch v-model="localEvent.is_www_expanded" />
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-4" v-if="localEvent.is_www_expanded">
            <div class="col-12">
              <label
                v-html="sanitize($t('EVENTS.LABEL.WWW_EVENT_PRICE'))"
              ></label>
              <HMTextInput
                type="number"
                v-model="localEvent.www_price"
                :rules="`required${canPostFreeWWWEvent ? '' : '|min_value:5'}`"
                :name="$t('EVENTS.LABEL.WWW_EVENT_PRICE_FIELD_NAME')"
              >
                <template slot="prepend">USD</template>
              </HMTextInput>
            </div>
            <div class="col-12">
              <label
                v-html="sanitize($t('EVENTS.LABEL.WWW_EVENT_CATEGORIES'))"
              ></label>
              <v-select
                :placeholder="$t('EVENTS.LABEL.SELECT_ONE_OR_MORE_CATEGORIES')"
                :options="eventCategories"
                :multiple="true"
                label="name"
                code="id"
                v-model="localEvent.www_event_categories"
              />
            </div>
            <div class="col-12 mt-4">
              <div
                class="form-group email-providers convertkit"
                v-if="flodeskSegments.length && isUsingFlodesk"
              >
                <label>{{ $t('PRODUCTS.LABELS.FLODESK_SEGMENT') }}</label>
                <v-select
                  :options="flodeskSegments"
                  label="name"
                  code="id"
                  v-model="localEvent.www_flodesk_segment"
                  :reduce="segment => segment.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.FLODESK_SEGMENT_DESCRIPTION')
                }}</span>
              </div>
              <div
                class="form-group email-providers convertkit"
                v-if="convertKitTags.length && isUsingConvertKit"
              >
                <label>{{ $t('PRODUCTS.LABELS.CONVERTKIT_TAG') }}</label>
                <v-select
                  :options="convertKitTagsMapped"
                  label="name"
                  code="id"
                  v-model="localEvent.www_convertkit_tag"
                  :reduce="tag => tag.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.CONVERTKIT_TAG_DESCRIPTION')
                }}</span>
              </div>
              <div
                class="form-group email-providers mailchimp"
                v-if="mailChimpLists.length && isUsingMailChimp"
              >
                <label>{{ $t('PRODUCTS.LABELS.MAILCHIMP_LIST') }}</label>
                <v-select
                  :options="mailChimpLists"
                  label="name"
                  code="id"
                  v-model="localEvent.www_mailchimp_list"
                  :reduce="list => list.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.MAILCHIMP_LIST_DESCRIPTION')
                }}</span>
                <HMTextInput
                  v-if="localEvent.mailchimp_list"
                  class="mt-3"
                  type="text"
                  v-model="localEvent.www_mailchimp_tag"
                  :placeholder="$t('PRODUCTS.LABELS.OPTIONAL_MAILCHIMP_TAG')"
                  :label="$t('EVENTS.TEXT.USE_MAILCHIMP_TAG')"
                  name="Mailchimp tag"
                />
              </div>
            </div>
            <div class="col-12 mb-3">
              <h4>
                {{ $t('EVENTS.LABEL.UPLOAD_EVENT_IMAGES') }}
              </h4>
              <p>
                {{ $t('EVENTS.TEXT.WE_REVIEW_EVERY_PHOTO') }}
              </p>
              <ul>
                <li>{{ $t('EVENTS.TEXT.HIGH_RESOLUTION') }}</li>
                <li>{{ $t('EVENTS.TEXT.NO_EXPLICIT_OR_OFFENSIVE_IMAGES') }}</li>
                <li>{{ $t('EVENTS.TEXT.CLEAR_NOT_BLURRY') }}</li>
                <li>{{ $t('EVENTS.TEXT.WELL_LIT_NOT_TOO_DARK') }}</li>
                <li>{{ $t('EVENTS.TEXT.NO_HEAVY_FILTERS') }}</li>
                <li>{{ $t('EVENTS.TEXT.NO_TEXT_OVERLAYS') }}</li>
                <li>{{ $t('EVENTS.TEXT.SINGLE_PHOTOS') }}</li>
                <li>
                  {{
                    $t(
                      'EVENTS.TEXT.PHOTOS_SHOULD_ATTEMPT_TO_DESCRIBE_AN_INTERESTING_ELEMENT'
                    )
                  }}
                </li>
                <li>{{ $t('EVENTS.TEXT.NO_SCREENSHOTS') }}</li>
              </ul>
              <p>
                {{ $t('EVENTS.TEXT.IF_YOU_ARE_UNABLE_TO_FIND_IMAGERY') }}
              </p>
              <p>
                {{
                  $t('EVENTS.TEXT.IF_IMAGERY_DOES_NOT_FOLLOW_THE_REQUIREMENTS')
                }}
              </p>
              <p class="d-flex flex-row align-items-center mt-4">
                <HMCheckBox
                  class="mr-3"
                  v-model="localEvent.agreed_to_www_requirements"
                />
                <span
                  @click="
                    localEvent.agreed_to_www_requirements = !localEvent.agreed_to_www_requirements
                  "
                >
                  {{
                    $t(
                      'EVENTS.TEXT.I_AGREE_TO_ADHERE_TO_THE_IMAGE_REQUIREMENTS'
                    )
                  }}
                </span>
              </p>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <HMImageUpload
                  inputName="www-photo1"
                  :title="`${$t('EVENTS.TEXT.UPLOAD_EVENT_PHOTO')} 1`"
                  :description="$t('EVENTS.TEXT.WWW_EVENT_PHOTOS_TEXT')"
                  :imageDescription="$t('PRODUCTS.LABELS.COVER_PHOTO')"
                  v-model="localEvent.www_photo1"
                  :canvaWidth="1024"
                  :canvaHeight="1024"
                  :focalPointPicker="true"
                />
              </div>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <HMImageUpload
                  inputName="www-photo2"
                  :title="`${$t('EVENTS.TEXT.UPLOAD_EVENT_PHOTO')} 2`"
                  :description="$t('EVENTS.TEXT.WWW_EVENT_PHOTOS_TEXT')"
                  :imageDescription="$t('PRODUCTS.LABELS.COVER_PHOTO')"
                  v-model="localEvent.www_photo2"
                  :canvaWidth="1024"
                  :canvaHeight="1024"
                  :focalPointPicker="true"
                />
              </div>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <HMImageUpload
                  inputName="www-photo3"
                  :title="`${$t('EVENTS.TEXT.UPLOAD_EVENT_PHOTO')} 3`"
                  :description="$t('EVENTS.TEXT.WWW_EVENT_PHOTOS_TEXT')"
                  :imageDescription="$t('PRODUCTS.LABELS.COVER_PHOTO')"
                  v-model="localEvent.www_photo3"
                  :canvaWidth="1024"
                  :canvaHeight="1024"
                  :focalPointPicker="true"
                />
              </div>
            </div>
            <div
              class="col-sm-12 mb-3"
              v-if="localEvent.www_admin_review_notes"
            >
              <h4>
                {{ $t('EVENTS.LABEL.MARVELOUS_REVIEW_NOTES') }}
              </h4>
              <div class="alert alert-light mb-1">
                {{ localEvent.www_admin_review_notes }}
              </div>
              <span class="d-block text-right">
                {{ $formatDate(localEvent.www_admin_review_date, 'date-word') }}
              </span>
            </div>
            <div class="col-12 justify-content-between d-flex flex-row">
              <h4>
                {{ $t('EVENTS.LABEL.PUBLISH_ON_WELLWELLWELL') }}
              </h4>
              <HMSwitch
                v-model="localEvent.is_www_event"
                :disabled="
                  !isEligibleForWWWEventPublishing && !localEvent.is_www_event
                "
                v-tooltip="{
                  content:
                    !isEligibleForWWWEventPublishing && !localEvent.is_www_event
                      ? $t('EVENTS.LABEL.PLEASE_SEE_FEEDBACK_BELOW')
                      : null,
                  delay: 100,
                  placement: 'top',
                  container: 'body',
                  trigger: 'click hover',
                }"
              />
            </div>
            <div v-if="!isEligibleForWWWEventPublishing" class="col-12 mt-3">
              <div class="alert alert-danger">
                {{ $t('EVENTS.TEXT.TO_POST_ON_WELLWELLWELL_YOU_NEED') }}
                <ul class="mb-0">
                  <li
                    v-if="!hasPaymentInfo"
                    v-html="
                      sanitize(
                        $t(
                          'EVENTS.TEXT.RECONNECT_YOUR_STRIPE_ACCOUNT_TO_PUBLISH_ON_WELLWELLWELL'
                        )
                      )
                    "
                  ></li>
                  <li v-if="!hasInstructors">
                    {{ $t('EVENTS.TEXT.ADD_INSTRUCTOR') }}
                  </li>
                  <li
                    v-if="!hasInstructorBio"
                    v-html="sanitize($t('EVENTS.TEXT.ADD_INSTRUCTOR_BIO'))"
                  ></li>
                  <li v-if="!hasEventImages">
                    {{ $t('EVENTS.TEXT.ADD_EVENT_IMAGES') }}
                  </li>
                  <li v-if="!hasEventDescription">
                    {{ $t('EVENTS.TEXT.ADD_EVENT_DESCRIPTION') }}
                  </li>
                  <li v-if="!localEvent.agreed_to_www_requirements">
                    {{
                      $t(
                        'EVENTS.TEXT.YOU_NEED_TO_AGREE_TO_THE_WWW_REQUIREMENTS'
                      )
                    }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <ValidationProvider
                  rules="required"
                  :name="$t('EVENTS.LABEL.WHICH_PRODUCT_CONTAIN_THIS_EVENT')"
                  v-slot="{ errors }"
                >
                  <label>{{
                    $t('EVENTS.LABEL.WHICH_PRODUCT_CONTAIN_THIS_EVENT')
                  }}</label>
                  <v-select
                    :options="teacherProductsWithPrivateSessions"
                    label="product_name"
                    code="id"
                    v-model="localEvent.products"
                    @input="privateProductSelected"
                    :reduce="product => [product]"
                  />
                  <p class="invalid-feedback d-block">{{ errors[0] }}</p>
                </ValidationProvider>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <ValidationProvider
                  rules="required"
                  :name="$t('EVENTS.LABEL.SELECT_A_STUDENT')"
                  v-slot="{ errors }"
                >
                  <label>{{ $t('EVENTS.LABEL.SELECT_A_STUDENT') }}</label>
                  <v-select
                    :options="productStudents"
                    label="label"
                    code="id"
                    v-model="localEvent.student"
                    required
                  />
                  <p class="invalid-feedback d-block">{{ errors[0] }}</p>
                </ValidationProvider>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label>{{
                  $t('EVENTS.LABEL.WHICH_INSTRUCTORS_WILL_LEAD_THIS_EVENT')
                }}</label>
                <v-select
                  :options="allInstructors"
                  multiple
                  label="name"
                  code="id"
                  v-model="localEvent.instructors"
                />
                <div v-if="maxInstructors === 1" class="d-inline-flex">
                  <span class="c-coral pointer small mr-2">
                    {{ $t('EVENTS.LABEL.ADD_SUBSTITUTES') }}
                  </span>
                  <HMUpgradeLock text="EVENTS.LABEL.ADD_SUBSTITUTES_LOCKED" />
                </div>
                <span
                  v-else-if="addSubstitutesLinkVisible"
                  class="c-coral pointer small"
                  @click="substitutesVisible = true"
                >
                  {{ $t('EVENTS.LABEL.ADD_SUBSTITUTES') }}
                </span>
                <div
                  v-if="displayInstructorSubstitutes"
                  class="form-group mt-3"
                >
                  <label>{{
                    $t(
                      'EVENTS.LABEL.WHICH_INSTRUCTORS_WILL_SUBSTITUTE_THIS_EVENT'
                    )
                  }}</label>
                  <v-select
                    :options="allInstructors"
                    multiple
                    label="name"
                    code="id"
                    v-model="localEvent.substitute_instructors"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <HMHTMLEditor
                  class="mt-1"
                  v-model="localEvent.event_description_new"
                  :htmlValue="localEvent.event_description"
                  :title="$t('GENERAL.LABEL.DESCRIPTION')"
                  :addContentText="$t('EVENTS.TEXT.ADD_TEXT_DESCRIPTION')"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">{{
                  $t('EVENTS.LABEL.EMAIL_NOTIFICATIONS')
                }}</label>
                <HMToggleButtonGroup
                  v-model="localEvent.email_notifications_enabled"
                >
                  <template slot="falseState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 0)
                    }}</span> </template
                  >>
                  <template slot="trueState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 1)
                    }}</span> </template
                  >>
                </HMToggleButtonGroup>
              </div>
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">{{
                  $t('EVENTS.LABEL.SMS_NOTIFICATIONS')
                }}</label>
                <HMToggleButtonGroup
                  v-model="localEvent.sms_notifications_enabled"
                >
                  <template slot="falseState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 0)
                    }}</span> </template
                  >>
                  <template slot="trueState">
                    <span>{{
                      $tc('GENERAL.LABEL.SEND_VARIANT', 1)
                    }}</span> </template
                  >>
                </HMToggleButtonGroup>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label
                  class="c-charcoal m-0 mr-4"
                  v-tooltip="{
                    content: $t('EVENTS.TEXT.POST_EVENT_RECORDING_TOOLTIP'),
                    delay: 100,
                    placement: 'top',
                    container: 'body',
                    trigger: 'click hover',
                  }"
                >
                  {{ $t('EVENTS.LABEL.MAKE_POST_EVENT_RECORDING_AVAILABLE') }}
                </label>
                <HMSwitch v-model="localEvent.post_event_recording_available" />
                <div v-if="localEvent.post_event_recording_available">
                  <span class="c-charcoal ml-3">for</span>
                  <b-form-select
                    v-model="localEvent.recording_expiration_days"
                    class="ml-3 w-auto"
                  >
                    <b-form-select-option
                      v-for="days in [
                        1,
                        2,
                        3,
                        4,
                        5,
                        6,
                        7,
                        8,
                        9,
                        10,
                        11,
                        12,
                        13,
                        14,
                      ]"
                      :key="days"
                      :value="days"
                    >
                      {{ $tc('GENERAL.LABEL.DAY_VARIANT', days) }}
                    </b-form-select-option>
                  </b-form-select>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group d-flex flex-row align-items-center">
                <label class="c-charcoal m-0 mr-4">
                  {{
                    $t(
                      'EVENTS.LABEL.COPY_EVENT_DESCRIPTION_TO_RECORDING_DESCRIPTION'
                    )
                  }}
                </label>
                <HMSwitch
                  v-model="localEvent.copy_event_description_to_recording"
                />
              </div>
            </div>
          </div>
        </template>
      </form>
    </template>
  </div>
</template>

<script>
import _ from 'lodash';
import { extend } from 'vee-validate';
import { ValidationProvider } from 'vee-validate';
import HMTimePicker from '../common/HMTimePicker';
import HMToggleButton from '../common/HMToggleButton';
import HMToggleButtonGroup from '../common/HMToggleButtonGroup';
import editorToolbar from '@/web/utils/editor-toolbar';
import {
  EVENT_TYPE_BROADCAST,
  EVENT_TYPES,
  DAYS_IN_WEEK,
  REGISTRATION_LIMITS,
  DURATION_OPTIONS,
  EVENT_TYPE_PRIVATE,
  EVENT_TYPE_COLLABORATIVE,
  EVENT_TYPE_LOCAL,
} from '@/core/utils/events';
import { errorToMessage } from '@/core/utils/errors';
import { mapGetters } from 'vuex';
import { formatDate } from '@/web/utils/format';
import HMCheckBox from '@/web/components/common/HMCheckBox';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';
import { cleanedFileName } from '@/core/utils/utils';
import { sanitize } from '@/core/utils/utils';
import HMTextInput from '../common/HMTextInput';
import HMSwitch from '@/web/components/common/HMSwitch.vue';

extend('duration', {
  params: ['target'],
  validate(value, { target }) {
    if (value === 0) return target > 0;
    return value > 0;
  },
  message: 'EVENTS.TEXT.DURATION_CAN_NOT_BE_ZERO',
});

export default {
  name: 'EventForm',
  components: {
    HMTextInput,
    HMCheckBox,
    ValidationProvider,
    HMTimePicker,
    HMToggleButton,
    HMToggleButtonGroup,
    HMSwitch,
  },
  mixins: [TeacherPermissionsMixin],
  props: {
    localEvent: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters({
      allTeacherProducts: 'product/allTeacherProducts',
      teacherProductsWithPrivateSessions:
        'product/teacherProductsWithPrivateSessions',
      allInstructors: 'mediaLibrary/instructors',
      emailProvider: 'setting/emailProvider',
      flodeskSegments: 'setting/flodeskSegments',
      mailChimpLists: 'setting/mailChimpLists',
      convertKitTags: 'setting/convertKitTags',
      isUsingFlodesk: 'setting/isUsingFlodesk',
      isUsingConvertKit: 'setting/isUsingConvertKit',
      isUsingMailChimp: 'setting/isUsingMailChimp',
      zoomInfo: 'user/zoomInfo',
      maxInstructors: 'user/maxInstructors',
      paymentInfo: 'setting/paymentInfo',
      eventCategories: 'event/eventCategories',
      canCreateWWWEvent: 'user/canCreateWWWEvent',
      canPostFreeWWWEvent: 'user/canPostFreeWWWEvent',
    }),
    hasOnePhotoForWWW() {
      return (
        this.localEvent?.cover_photo ||
        this.localEvent?.www_photo1 ||
        this.localEvent?.www_photo2 ||
        this.localEvent?.www_photo3
      );
    },
    isInPersonEvent() {
      return this.localEvent?.event_type === EVENT_TYPE_LOCAL;
    },
    isEligibleForWWWEventPublishing() {
      return (
        this.hasPaymentInfo &&
        this.hasInstructors &&
        this.hasInstructorBio &&
        this.hasEventDescription &&
        this.hasEventImages &&
        this.localEvent.agreed_to_www_requirements
      );
    },
    hasPaymentInfo() {
      return (
        this.paymentInfo?.payment_gateway === 'stripe' &&
        !!this.paymentInfo?.stripe_user_id
      );
    },
    hasInstructors() {
      return this.localEvent?.instructors?.length;
    },
    hasInstructorBio() {
      return this.localEvent?.instructors?.every(
        instructor => instructor.bio || instructor.bio_new
      );
    },
    hasEventDescription() {
      return (
        JSON.parse(this.localEvent?.event_description_new || '{}')?.blocks
          ?.length > 0
      );
    },
    hasEventImages() {
      return (
        this.localEvent?.www_photo1 ||
        this.localEvent?.www_photo2 ||
        this.localEvent?.www_photo3
      );
    },
    hasSubstituteInstructors() {
      return this.localEvent?.substitute_instructors?.length;
    },
    addSubstitutesLinkVisible() {
      if (this.hasSubstituteInstructors) return false;
      return !this.substitutesVisible;
    },
    displayInstructorSubstitutes() {
      return this.substitutesVisible || this.hasSubstituteInstructors;
    },
    hasMultipleZoomConnections() {
      return this.zoomInfo && this.zoomInfo.length > 1;
    },
    eventProducts() {
      if (!this.allTeacherProducts) return [];
      return this.allTeacherProducts.filter(
        product =>
          ['punch_card', 'bundle'].indexOf(product.product_type) === -1 &&
          !product.archived
      );
    },
    eventClassCardProducts() {
      if (!this.allTeacherProducts) return [];
      return this.allTeacherProducts.filter(
        product =>
          ['punch_card'].indexOf(product.product_type) !== -1 &&
          !product.archived
      );
    },
    eventTypes() {
      return EVENT_TYPES.map(et => {
        return this.mapEventType(et);
      });
    },
    daysInWeek() {
      return DAYS_IN_WEEK;
    },
    canShowFreeEventOption() {
      return (
        this.isCollaborativeOrBroadcastOrLocalStudioEvent &&
        this.hasPermission('has_free_events')
      );
    },
    isCollaborativeOrBroadcastOrLocalStudioEvent() {
      const type = this.localEvent.event_type;
      return [
        EVENT_TYPE_COLLABORATIVE,
        EVENT_TYPE_BROADCAST,
        EVENT_TYPE_LOCAL,
      ].some(t => t === type);
    },
    getMaxRegistrations() {
      return Math.max(...REGISTRATION_LIMITS);
    },
    registrationLimits() {
      return REGISTRATION_LIMITS.filter(
        limit => limit <= this.getMaxRegistrations
      );
    },
    isStreamingEvent() {
      return [EVENT_TYPE_COLLABORATIVE, EVENT_TYPE_BROADCAST].some(
        t => t === this.localEvent.event_type
      );
    },
    isRegistrationLimitDisabled() {
      return this.localEvent.is_free_event && this.isStreamingEvent;
    },
    durationOptions() {
      return DURATION_OPTIONS;
    },
    isPrivateEvent() {
      return this.localEvent.event_type === EVENT_TYPE_PRIVATE;
    },
    isRepeatingEvent() {
      return this.localEvent.event_schedule !== null;
    },
    isNewEvent() {
      return _.get(this.localEvent, 'id', null) == null;
    },
    isCalendlyEvent() {
      return this.localEvent.calendly_id;
    },
    getFreeEventDisabledTooltip() {
      if (!this.localEvent) return null;
      if (!this.localEvent.registrations) return null;
      if (this.localEvent.registrations?.length === 0) return null;

      return this.$t(
        'EVENTS.TEXT.FREE_EVENT_TOGGLE_ONLY_ALLOWED_IF_NO_REGISTRATIONS_YET'
      );
    },
    hasRegistrations() {
      if (!this.localEvent) return false;
      if (!this.localEvent.registrations) return false;
      return this.localEvent.registrations.length > 0;
    },
    convertKitTagsMapped() {
      return this.convertKitTags.map(tag => {
        return {
          ...tag,
          id: `${tag.id}`,
        };
      });
    },
    minDate() {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return today;
    },
    maxDate() {
      const maxDate = new Date(this.minDate);
      maxDate.setMonth(maxDate.getMonth() + 12);
      return maxDate;
    },
  },
  data() {
    return {
      selectedEventType: this.mapEventType(
        EVENT_TYPES.filter(et => et.code === this.localEvent.event_type)[0]
      ),
      customToolbar: editorToolbar,
      productStudents: [],
      substitutesVisible: false,
    };
  },
  methods: {
    sanitize,
    save() {
      const event = { ...this.localEvent };
      event.attachments = event.attachments?.map(attachment => attachment.id);
      this.$emit('save', event);
    },
    setSelectedEventType(selectedEventType) {
      this.localEvent.event_type = _.get(selectedEventType, 'code', '');
    },
    mapEventType(eventType) {
      return {
        label: this.$t(eventType.label),
        code: eventType.code,
        icon: eventType.icon,
      };
    },
    formatDate(date) {
      return formatDate(date, 'date-word');
    },
    formatTime(datetime) {
      return formatDate(datetime, 'time');
    },
    async privateProductSelected() {
      this.productStudents = [];
      if (!this.localEvent.products || this.localEvent.products.length == 0)
        return;
      const productId = this.localEvent.products[0].id;
      try {
        const result = await this.$store.dispatch(
          'product/getProductStudents',
          productId
        );
        this.productStudents = result;
        this.productStudents = this.productStudents.map(student => {
          return {
            ...student,
            label: `${student.first_name} ${student.last_name} (${student.email})`,
          };
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load product students',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    updateFreeEventProperties(isFreeEvent) {
      this.localEvent.registration_required = isFreeEvent;
      if (isFreeEvent) {
        if (this.isStreamingEvent) {
          this.localEvent.registration_limit = this.getMaxRegistrations;
        } else {
          this.localEvent.registration_limit = 0;
        }
        this.localEvent.post_event_recording_available = false;
        this.localEvent.auto_notify_waitlist = false;
        this.localEvent.is_punchable_event = false;
        this.localEvent.classcards = [];
        this.localEvent.is_www_event = false;
      }
    },
    addFile(file) {
      this.localEvent.attachments.push(file);
    },
    removeFile(fileIndex) {
      this.localEvent.attachments.splice(fileIndex, 1);
    },
    downloadAttachment(file) {
      return window.open(file.storage_url, '_blank');
    },
    cleanedFileName,
  },
  watch: {
    'localEvent.registration_required': function (value) {
      if (!value) {
        this.localEvent.registration_limit = 0;
        this.localEvent.registration_closes_minutes_before_start = 0;
        this.localEvent.unregistration_closes_minutes_before_start = 0;
        this.auto_notify_waitlist = false;
      }
    },
    'localEvent.is_free_event': function (value) {
      if (value) {
        this.updateFreeEventProperties(value);
      }
    },
    'localEvent.event_type': function (value) {
      if (this.localEvent.is_free_event) {
        this.updateFreeEventProperties(this.localEvent.is_free_event);
      }
    },
  },
  async mounted() {
    if (!this.isNewEvent) {
      await this.privateProductSelected();
    }

    try {
      this.$store.dispatch('product/getTeacherProducts');
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to load products',
        text: errorToMessage(error.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    }
    try {
      this.$store.dispatch('product/getTeacherProductsWithPrivateSessions');
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to load products',
        text: errorToMessage(error.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    }
    try {
      this.$store.dispatch('mediaLibrary/getInstructors');
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to load instructors',
        text: errorToMessage(error.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    }
    this.$store.dispatch('setting/getEmailProvider');
    this.$store.dispatch('setting/getFlodeskSegments');
    this.$store.dispatch('setting/getMailChimpLists');
    this.$store.dispatch('setting/getConvertKitTags');
    this.$store.dispatch('setting/getPaymentInfo');
    this.$store.dispatch('event/getEventCategories');
    this.$store.dispatch('setting/getGoogleMeetConnections');
    this.$store.dispatch('setting/getZoomConnections');
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

::v-deep .hm-upgrade-lock {
  #upgrade-trigger {
    width: 24px;
    height: 24px;
    font-size: 0.8rem;
  }
}
.www-card {
  .www-logo {
    max-width: 300px;
  }

  background-color: lighten($coral, 25%) !important;
}
</style>
